import React, { useState, useEffect } from "react";
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  Button,
  Box,
  Typography,
  Paper,
  Divider,
} from "@mui/material";
import Grid from '@mui/material/GridLegacy';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

/**
 * A dynamic form generator that creates a form based on a provided schema
 *
 * @param {Object} props
 * @param {Object} props.formSchema - Schema describing form fields and their properties
 * @param {function} props.onSubmit - Function to call when form is submitted
 * @param {string} props.submitButtonText - Text to display on submit button (optional)
 * @param {string} props.title - Form title (optional)
 * @returns {React.Component}
 */
const DynamicForm = ({ formSchema, onSubmit, submitButtonText = "Submit", title = "Form" }) => {
  // Initialize form values based on schema
  const initialValues = {};
  Object.keys(formSchema).forEach((fieldName) => {
    initialValues[fieldName] = formSchema[fieldName].defaultValue || "";
  });

  const [formValues, setFormValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  const groupFieldsInPairs = (fields) => {
    const fieldNames = Object.keys(fields);
    const pairs = [];

    for (let i = 0; i < fieldNames.length; i++) {
      const currentField = fields[fieldNames[i]];

      // Skip if this field was already paired
      if (pairs.flat().includes(fieldNames[i])) continue;

      // If current field is a date and next field is also a date, pair them
      if (i < fieldNames.length - 1) {
        const nextField = fields[fieldNames[i + 1]];

        const shouldPair = currentField.pair_with === fieldNames[i + 1] || nextField.pair_with === fieldNames[i];

        if (shouldPair) {
          pairs.push([fieldNames[i], fieldNames[i + 1]]);
          continue;
        }
      }

      // If not paired, add as a single field
      if (!pairs.flat().includes(fieldNames[i])) {
        pairs.push([fieldNames[i]]);
      }
    }

    return pairs;
  };

  const handleChange = (fieldName, value) => {
    setFormValues({
      ...formValues,
      [fieldName]: value,
    });

    // Clear error when field is modified
    if (errors[fieldName]) {
      const newErrors = { ...errors };
      delete newErrors[fieldName];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    Object.keys(formSchema).forEach((fieldName) => {
      const field = formSchema[fieldName];

      // Check required fields
      if (field.required && !formValues[fieldName]) {
        newErrors[fieldName] = "This field is required";
        isValid = false;
      }

      // Check custom validation
      if (field.validate && formValues[fieldName]) {
        const validationResult = field.validate(formValues[fieldName]);
        if (validationResult !== true) {
          newErrors[fieldName] = validationResult;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formValues);
    }
  };

  const renderField = (fieldName, fieldConfig) => {
    const { form_type, label = fieldName, placeholder, helperText, required, disabled } = fieldConfig;

    type Margins = "normal" | "none" | "dense";
    const commonProps = {
      fullWidth: true,
      id: fieldName,
      name: fieldName,
      label: label,
      placeholder: placeholder,
      required: required,
      disabled: disabled,
      error: !!errors[fieldName],
      helperText: errors[fieldName] || helperText,
      value: formValues[fieldName] || "",
      margin: "normal" as Margins,
      inputProps: {
        sx: {
          bgcolor: "white",
        },
      },
    };

    switch (form_type) {
      case "text":
      case "text input":
        return <TextField {...commonProps} onChange={(e) => handleChange(fieldName, e.target.value)} />;

      case "number":
        return <TextField {...commonProps} type="number" onChange={(e) => handleChange(fieldName, e.target.value)} />;

      case "email":
        return <TextField {...commonProps} type="email" onChange={(e) => handleChange(fieldName, e.target.value)} />;

      case "password":
        return <TextField {...commonProps} type="password" onChange={(e) => handleChange(fieldName, e.target.value)} />;

      case "textarea":
        return (
          <TextField {...commonProps} multiline rows={4} onChange={(e) => handleChange(fieldName, e.target.value)} />
        );

      case "selection":
      case "select":
        return (
          <FormControl
            fullWidth
            margin="normal" // Using the correct literal type
            error={!!errors[fieldName]}
            required={required}
            disabled={disabled}
          >
            <InputLabel id={`${fieldName}-label`}>{label}</InputLabel>
            <Select
              labelId={`${fieldName}-label`}
              id={fieldName}
              value={formValues[fieldName] || ""}
              label={label}
              onChange={(e) => handleChange(fieldName, e.target.value)}
              inputProps={{
                sx: {
                  bgcolor: "white",
                },
              }}
            >
              {fieldConfig.selections?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {(errors[fieldName] || helperText) && <FormHelperText>{errors[fieldName] || helperText}</FormHelperText>}
          </FormControl>
        );
      case "date":
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={label}
              value={formValues[fieldName] || null}
              onChange={(newValue) => handleChange(fieldName, newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  margin: "normal",
                  required: required,
                  disabled: disabled,
                  error: !!errors[fieldName],
                  helperText: errors[fieldName] || helperText,
                  inputProps: {
                    sx: {
                      bgcolor: "white",
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        );

      default:
        return <Typography color="error">Unknown field type: {form_type}</Typography>;
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      {title && (
        <Typography variant="h3" component="h2" gutterBottom align="center">
          {title}
        </Typography>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        {Object.keys(formSchema).map((sectionKey, sectionIndex) => {
          const section = formSchema[sectionKey];
          const fields = section.fields || {};
          const fieldPairs = groupFieldsInPairs(fields);

          return (
            <Paper
              key={sectionKey}
              elevation={0}
              sx={{
                mb: 3,
                p: 2,
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid #e0e0e0",
              }}
            >
              {/* Section Header */}
              {section.title && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    {section.title}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </>
              )}

              {/* Section Fields */}
              <Grid container spacing={2}>
                {fieldPairs.map((pair, index) => (
                  <Grid item xs={12} key={`group-${sectionKey}-${index}`}>
                    <Grid container spacing={2}>
                      {pair.map((fieldName) => (
                        <Grid item xs={12} sm={pair.length > 1 ? 6 : 12} key={fieldName}>
                          {renderField(fieldName, fields[fieldName])}
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          );
        })}

        {/* Submit Button */}
        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Button type="submit" variant="contained" fullWidth size="large" style={{ backgroundColor: "#004e9d" }}>
              {submitButtonText}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default DynamicForm;
